import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from 'reselect';
import {
  fetchAllChallenges,
  fetchChallengeIntro,
  updateChallengeIntro,
  sortChallenges,
} from "../../actions/challengeActions";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  TextField,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import SortableChallenge from "./SortableChallenge";

const selectChallenges = createSelector(
  state => state.challenges.challenges,
  challenges => challenges ? Object.values(challenges) : []
);

const ChallengeTable = ({ searchText }) => {
  const dispatch = useDispatch();
  const challenges = useSelector(selectChallenges);
  const challengeIntroText = useSelector((state) => state.general_data.general_data?.challenge_intro_text || "");

  const [sortedChallenges, setSortedChallenges] = useState([]);
  const [introText, setIntroText] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    dispatch(fetchChallengeIntro());
    dispatch(fetchAllChallenges());
  }, [dispatch]);

  useEffect(() => {
    if (challenges.length > 0) {
      const sorted = [...challenges].sort(
        (a, b) => Number(a.challenge_sort_order) - Number(b.challenge_sort_order)
      );
      setSortedChallenges(sorted);
    }
  }, [challenges]);

  useEffect(() => {
    setIntroText(challengeIntroText);
  }, [challengeIntroText]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedChallenges.findIndex((item) => item.challenge_id === active.id);
      const newIndex = sortedChallenges.findIndex((item) => item.challenge_id === over.id);
      const newChallenges = arrayMove(sortedChallenges, oldIndex, newIndex);
      setSortedChallenges(newChallenges);
      const sortJson = newChallenges.map((obj) => obj.challenge_id);
      dispatch(sortChallenges(JSON.stringify(sortJson)));
    }
  };

  const handleIntroTextChange = (e) => {
    setIntroText(e.target.value);
  };

  const saveChallengeIntroText = () => {
    dispatch(updateChallengeIntro(introText));
  };

  if (!sortedChallenges.length) {
    return <h1 style={{ textAlign: "center" }}>Loading</h1>;
  }

  const filteredChallenges = searchText
    ? sortedChallenges.filter((challenge) =>
      challenge.challenge_title.toLowerCase().includes(searchText.toLowerCase())
    )
    : sortedChallenges;

  return (
    <>
      <Box sx={{ mt: 3, mb: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              value={introText}
              onChange={handleIntroTextChange}
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              label="Challenges Intro"
              placeholder="Enter challenges introduction text..."
            />
            <Button
              variant="contained"
              onClick={saveChallengeIntroText}
              startIcon={<Save />}
              color="success"
            >
              Update
            </Button>
          </Box>
        </Paper>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Sort Order</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Total Joins</strong></TableCell>
                <TableCell><strong>Total Sessions</strong></TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext
                items={filteredChallenges.map((item) => item.challenge_id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredChallenges.map((challenge) => (
                  <SortableChallenge
                    key={challenge.challenge_id}
                    id={challenge.challenge_id}
                    challenge={challenge}
                  />
                ))}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
    </>
  );
};

export default ChallengeTable;
